import { useNavigate } from 'react-router-dom'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import { readPolicyFromListCache } from '../utils/policiesListCache'
import { policyIsUnsupportedVehicleType } from '../utils/policyType'

export type PolicyTab = 'policy' | 'payment' | 'claims' | 'documents'

interface PolicyTabNavProps {
  policyId: string | undefined
  activeTab: PolicyTab
  policyForClaimsNav?: PolicyLink | null
}

const ACTIVE_TAB_CLASS =
  'bg-background-primary border border-border-primary border-solid shadow-xs flex gap-[8px] h-[44px] items-center justify-center md:justify-start overflow-clip px-[12px] py-[8px] relative rounded-md shrink-0 flex-1 md:w-full'

const INACTIVE_TAB_CLASS =
  'flex gap-[8px] h-[44px] items-center justify-center md:justify-start overflow-clip px-[12px] py-[8px] relative rounded-md shrink-0 flex-1 md:w-full'

function resolvedPolicyRef(policyId: string | undefined): string | null {
  if (!policyId) return null
  return decodePolicyRef(policyId) ?? policyId
}

export default function PolicyTabNav({ policyId, activeTab, policyForClaimsNav }: PolicyTabNavProps) {
  const navigate = useNavigate()
  const ref = resolvedPolicyRef(policyId)
  const claimsAllowed = !policyIsUnsupportedVehicleType(policyForClaimsNav)

  const navigateToClaims = () => {
    if (!ref) return
    const policy = policyForClaimsNav ?? readPolicyFromListCache(ref)
    navigate(`/claims?policyRef=${encodeURIComponent(ref)}`, {
      state: { preserveScroll: true, policy },
    })
  }

  return (
    <div className="bg-background-secondary-alt border border-border-secondary border-solid flex flex-row md:flex-col gap-[2px] items-center md:items-start justify-center relative rounded-md shrink-0 w-full md:w-[330px]">
      <button
        type="button"
        onClick={() => ref && navigate(`/policy/${encodeURIComponent(ref)}`, { state: { preserveScroll: true } })}
        className={activeTab === 'policy' ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS}
      >
        <p className="font-body font-semibold text-md leading-md text-text-secondary-700">Policy</p>
      </button>
      <button
        type="button"
        onClick={() => ref && navigate(`/payment/${encodeURIComponent(ref)}`, { state: { preserveScroll: true } })}
        className={activeTab === 'payment' ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS}
      >
        <p className="font-body font-semibold text-md leading-md text-text-secondary-700">Payment</p>
      </button>
      {claimsAllowed && (
        <button
          type="button"
          onClick={navigateToClaims}
          className={activeTab === 'claims' ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS}
        >
          <p className="font-body font-semibold text-md leading-md text-text-secondary-700">Claims</p>
        </button>
      )}
      <button
        type="button"
        onClick={() => ref && navigate(`/documents/${encodeURIComponent(ref)}`, { state: { preserveScroll: true } })}
        className={activeTab === 'documents' ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS}
      >
        <p className="font-body font-semibold text-md leading-md text-text-secondary-700">Documents</p>
      </button>
    </div>
  )
}
